import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, Check, X } from "lucide-react";

/**
 * Route: /admin/verifications
 * Admin review queue for veteran verification requests.
 */
export default function AdminVerifications() {
    const { user } = useAuth();
    const [status, setStatus] = useState("pending");
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(false);
    const [blobs, setBlobs] = useState({});
    const isAdmin = user && (user.role === "admin" || user.email === "admin@atlas.app");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/verifications", { params: { status } });
            setRows(data.requests || []);
        } catch (ex) {
            toast.error(ex.response?.data?.detail || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

    // Fetch each composited image as a blob so the img can render behind auth
    useEffect(() => {
        if (!rows) return;
        const pending = rows.filter((r) => r.composited_url && !blobs[r.id]);
        let cancelled = false;
        Promise.all(
            pending.map(async (r) => {
                try {
                    const { data } = await api.get(r.composited_url.replace(/^\/api/, ""), { responseType: "blob" });
                    return [r.id, URL.createObjectURL(data)];
                } catch {
                    return null;
                }
            })
        ).then((results) => {
            if (cancelled) return;
            const next = { ...blobs };
            results.filter(Boolean).forEach(([id, url]) => (next[id] = url));
            setBlobs(next);
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [rows]);

    const act = async (row, action) => {
        const notes = action === "reject" ? window.prompt("Reason (shown to the user):") || "" : "";
        try {
            await api.post(`/admin/verifications/${row.id}/${action}`, { notes });
            toast.success(action === "approve" ? "Approved — 50% off applied" : "Rejected");
            load();
        } catch (ex) {
            toast.error(ex.response?.data?.detail || "Failed");
        }
    };

    if (user === undefined) return null;
    if (!isAdmin) return <Navigate to="/" replace />;

    return (
        <div className="min-h-screen bg-slate-50">
            <TopBar />
            <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8" data-testid="admin-verifications-page">
                <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
                    <div>
                        <h1 className="font-display font-black text-4xl tracking-tighter text-slate-900 inline-flex items-center gap-3">
                            <ShieldCheck className="w-8 h-8" /> Verifications
                        </h1>
                        <p className="text-sm text-slate-600 mt-1">
                            Veteran service-record uploads. Approve to apply 50% off forever.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="field-input py-2 text-sm"
                            data-testid="admin-verifications-status"
                        >
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="all">All</option>
                        </select>
                        <button onClick={load} className="arcade-btn" data-testid="admin-verifications-refresh">
                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                        </button>
                    </div>
                </header>

                {rows && rows.length === 0 && (
                    <div className="crystal rounded-3xl p-10 text-center text-slate-500" data-testid="admin-verifications-empty">
                        No {status === "all" ? "" : status} verifications right now.
                    </div>
                )}

                {rows && rows.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {rows.map((r) => (
                            <article
                                key={r.id}
                                className="crystal rounded-3xl p-4"
                                data-testid={`admin-verification-${r.id}`}
                            >
                                <header className="flex items-baseline justify-between mb-2">
                                    <div className="min-w-0">
                                        <div className="font-display font-bold text-slate-900 truncate">
                                            {r.user_name || r.user_email}
                                        </div>
                                        <div className="text-xs text-slate-500 font-mono truncate">{r.user_email}</div>
                                    </div>
                                    <span className={`chip text-xs ${
                                        r.status === "pending" ? "bg-amber-100 text-amber-800" :
                                        r.status === "approved" ? "bg-emerald-100 text-emerald-800" :
                                        "bg-rose-100 text-rose-800"
                                    }`}>
                                        {r.status}
                                    </span>
                                </header>
                                {blobs[r.id] ? (
                                    <img
                                        src={blobs[r.id]}
                                        alt="Redacted record"
                                        className="w-full rounded-lg border border-slate-200"
                                        data-testid={`admin-verification-img-${r.id}`}
                                    />
                                ) : r.composited_url ? (
                                    <div className="w-full h-48 rounded-lg bg-slate-100 animate-pulse" />
                                ) : (
                                    <div className="text-xs text-slate-500 italic p-4">No redacted image yet</div>
                                )}
                                {r.admin_notes && (
                                    <div className="mt-2 text-xs text-slate-600 italic">
                                        Notes: {r.admin_notes}
                                    </div>
                                )}
                                {r.status === "pending" && (
                                    <div className="mt-3 flex gap-2 justify-end">
                                        <button onClick={() => act(r, "reject")} className="arcade-btn text-xs" data-testid={`verification-reject-${r.id}`}>
                                            <X className="w-3.5 h-3.5" /> Reject
                                        </button>
                                        <button onClick={() => act(r, "approve")} className="arcade-btn text-xs" data-variant="primary" data-testid={`verification-approve-${r.id}`}>
                                            <Check className="w-3.5 h-3.5" /> Approve · apply 50%
                                        </button>
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
